import { Nullable } from "../Extensions";
import { JNativeStatic } from "../JNative";
import { ArrayHelper } from "./ArrayHelper";
import { DateHelper } from "./DateHelper";
import { NAMESPACE } from "./_Namespace";

interface IStringComparisionOptions
{
    ignoreCase?: boolean;
}

function formatNode(node: Element, level: number, indentString: string = "    ", lineTerminator: string = "\r\n")
{
    const
        indentBefore = new Array(level++ + 1).join(indentString),
        indentAfter = new Array(level - 1).join(indentString);

    let
        textNode;

    for (let i = 0; i < node.children.length; i++)
    {
        textNode = document.createTextNode(lineTerminator + indentBefore);
        node.insertBefore(textNode, node.children[i]);

        if (node.children[i].nodeName.toLowerCase() !== "a")
            formatNode(node.children[i], level);

        if (node.lastElementChild == node.children[i])
        {
            textNode = document.createTextNode(lineTerminator + indentAfter);
            node.appendChild(textNode);
        }
    }

    return node;
}

export class StringHelper
{
    public static classNamespace = NAMESPACE + "StringHelper";
    public static className = "StringHelper";
    public static get classFullName() { return `${this.classNamespace}.${this.className}`; }

    // #region Global Variables

    public static readonly empty = "";

    // #endregion

    // #region Public Methods

    public static byteLength(s: string)
    {
        let
            length = s.length;

        for (let i = s.length - 1; i >= 0; i--)
        {
            const
                code = s.charCodeAt(i);

            if (code > 0x7f && code <= 0x7ff)
                length++;
            else if (code > 0x7ff && code <= 0xffff)
                length += 2;

            if (code >= 0xDC00 && code <= 0xDFFF)
                i--; // trail surrogate
        }
        return length;
    }
    public static compare(s1: string, s2: string)
    {
        if (s1 < s2)
            return -1;

        if (s1 > s2)
            return 1;

        return 0;
    }
    public static contains(s1: string, s2: string)
    {
        return s1.indexOf(s2) >= 0;
    }
    public static endsWith(s1: string, s2: string, stringComparisionOptions?: IStringComparisionOptions)
    {
        if (s2.length > s1.length)
            return false;

        return this.equals(s1.substring(s1.length - s2.length, s1.length), s2, stringComparisionOptions);
    }
    public static equals(s1: string, s2: string, stringComparisionOptions?: IStringComparisionOptions)
    {
        if (stringComparisionOptions?.ignoreCase)
            return s1.toLowerCase() === s2.toLowerCase();

        return String(s1) === s2;
    }
    public static format(...args: any[])
    {
        const
            s = args[0] as string;

        args = ArrayHelper.skip(args, 1);

        return s.replace(/{(\d+)}/g, (match, number) =>
        {
            if (typeof args[number] === "undefined")
                return match;

            if (args[number] === null)
                return "";

            return args[number];
        });
    }
    public static formatWithHtmlEncode(...args: any[])
    {
        return this.format.apply(
            this,
            args
                .map((item, index) =>
                {
                    if (index === 0)
                        return item;

                    return (
                        item !== undefined && item !== null && item.toString
                            ? this.htmlEncode(item.toString())
                            : null
                    );
                })
        );
    }
    public static fromDashed(s: string)
    {
        if (this.isNullOrUndefined(s))
            return s;

        return s.replace(/-(.)/g, function (_, group)
        {
            return group.toUpperCase();
        });
    }
    public static htmlBeautify(s?: string)
    {
        if (typeof s !== "string")
            return this.empty;
            
        const
            div = document.createElement('div');

        div.innerHTML = s.trim();

        return formatNode(div, 0).innerHTML.trim();
    }
    public static htmlDecode(s: string)
    {
        if (this.isNullOrUndefined(s))
            return s;

        return (
            s
                .toString()
                .replace(/&quot;/g, "\"")
                .replace(/&#39;/g, "'")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .replace(/&amp;/g, "&")
        );
    }
    public static htmlEncode(s: string)
    {
        if (this.isNullOrUndefined(s))
            return s;

        return (
            s
                .toString()
                .replace(/&/g, "&amp;")
                .replace(/>/g, "&gt;")
                .replace(/</g, "&lt;")
                .replace(/'/g, "&#39;")
                .replace(/"/g, "&quot;")
        );
    }
    public static invertColorHexCode(s: string, amplifyToBlackOrWhite?: boolean)
    {
        if (s.indexOf("#") === -1)
            s = `#${s}`;

        if (s.length === 4)
            s = s[0] + s[1] + s[1] + s[2] + s[2] + s[3] + s[3];

        if (!this.isColorHexCode(s))
            throw new Error("String is not a color hex code");

        s = s.slice(1);

        const
            r = parseInt(s.slice(0, 2), 16),
            g = parseInt(s.slice(2, 4), 16),
            b = parseInt(s.slice(4, 6), 16);

        if (amplifyToBlackOrWhite)
            return (r * 0.299 + g * 0.587 + b * 0.114) > 186 ? '#000000' : '#FFFFFF';

        const
            rString = (255 - r).toString(16),
            gString = (255 - g).toString(16),
            bString = (255 - b).toString(16);

        return "#" + this.padLeft(rString, 2, "0") + this.padLeft(gString, 2, "0") + this.padLeft(bString, 2, "0");
    }
    public static isColorHexCode(s: string)
    {
        return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(s);
    }
    public static isEmailAddress(s: string)
    {
        const
            $$input = JNativeStatic.create$$({ nodeName: "input", attributes: { type: "email" } });

        $$input.val(s);

        return ($$input[0] as HTMLInputElement).validity.valid;
    }
    public static isGuid(s: Nullable<string | undefined>)
    {
        return s && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
    }
    public static isDate(s: string, format: string)
    {
        let
            result = DateHelper.tryFromString(s, format);

        if (!result.success && this.contains(format, "f"))
            result = DateHelper.tryFromString(s, format.replace(/[.f]/gi, this.empty));

        return result.success;
    }
    public static isLowerCase(s: string)
    {
        const primitive = s.valueOf();
        return primitive === primitive.toLowerCase();
    }
    public static isNull(s: Nullable<string | undefined>)
    {
        return s === null;
    }
    public static isNullOrEmpty(s: Nullable<string | undefined>)
    {
        return this.isNull(s) || s === this.empty;
    }
    public static isNullOrUndefined(s: Nullable<string | undefined>)
    {
        return this.isNull(s) || this.isUndefined(s);
    }
    public static isNullOrUndefinedOrWhitespace(s: Nullable<string | undefined>)
    {
        return this.isNullOrWhitespace(s) || this.isUndefined(s);
    }
    public static isNullOrWhitespace(s: Nullable<string | undefined>)
    {
        if (s === undefined)
            return false;

        return this.isNull(s) || !s!.trim().length;
    }
    public static isTime(s: string)
    {
        return this.isDate(`1900-01-01T${s}`, "yyyy-MM-ddTHH:mm:ss");
    }
    public static isUndefined(s: Nullable<string | undefined>)
    {
        return s === undefined;
    }
    public static isUpperCase(s: string)
    {
        const primitive = s.valueOf();
        return primitive === primitive.toUpperCase();
    }
    public static padLeft(s: string, length: number, char: string)
    {
        let
            output = s;

        if (length > 0 && char.length > 0)
        {
            while (output.length < length)
                output = char + output;
        }

        return output;
    }
    public static padRight(s: string, length: number, char: string)
    {
        let
            output = s;

        if (length > 0 && char.length > 0)
        {
            while (output.length < length)
                output = output + char;
        }

        return output;
    }
    public static removeScriptTags(s: string)
    {
        const
            div = document.createElement("div");

        div.innerHTML = s;

        const
            scripts = div.getElementsByTagName("script");

        let
            i = scripts.length;

        while (i--)
            scripts[i].parentNode?.removeChild(scripts[i]);

        return div.innerHTML;
    }
    public static startsWith(s1: string, s2: string, stringComparisionOptions?: IStringComparisionOptions)
    {
        if (s2.length > s1.length)
            return false;

        return this.equals(s1.substring(0, s2.length), s2, stringComparisionOptions);
    }
    public static toDashed(s: string)
    {
        if (this.isNullOrUndefined(s))
            return s;

        return s.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
    }
    public static toLowerCamelCase(s: string)
    {
        if (this.isNullOrEmpty(s))
            return s;

        if (!this.isUpperCase(s[0]))
            return s;

        const
            chars = s.split(this.empty);

        for (let i = 0; i < chars.length; i++)
        {
            const
                hasNext = i + 1 < chars.length;

            if (i > 0 && hasNext && !this.isUpperCase(chars[i + 1]))
                break;

            chars[i] = chars[i].toLowerCase();
        }

        return chars.join(this.empty);
    }
    public static toLowerCamelCasePath(s: string)
    {
        if (this.isNullOrEmpty(s))
            return s;

        return (
            s
                .split(".")
                .map(item => this.toLowerCamelCase(item))
                .join(".")
        );
    }
    public static toUpperCamelCase(s: string)
    {
        return (
            s.substring(0, 1).toUpperCase()
            + (s.length > 1 ? s.substring(1) : this.empty)
        );
    }
    public static trim(s: string, char: string | string[])
    {
        return this.trimEnd(this.trimStart(s, char), char);
    }
    public static trimEnd(s: string, char: string | string[])
    {
        let
            newString = s.toString();

        if (char === undefined)
            char = [" ", "\r", "\n"];

        const
            charArray = ArrayHelper.isArrayLike(char) ? char : [char];

        while (ArrayHelper.contains(charArray, newString[newString.length - 1]))
            newString = newString.substring(0, newString.length - 1);

        return newString;
    }
    public static trimStart(s: string, char: string | string[])
    {
        let
            newString = s.toString();

        if (char === undefined)
            char = [" ", "\r", "\n"];

        const
            charArray = ArrayHelper.isArrayLike(char) ? char : [char];

        while (ArrayHelper.contains(charArray, newString[0]))
            newString = newString.substring(1);

        return newString;
    }
    public static truncate(s: string, length: number, elipsis?: string)
    {
        s = (s || this.empty).trim();
        length = length ?? 10;
        elipsis = elipsis ?? "...";

        if (s.length <= length)
            return s;

        const
            words = s.split(" ");

        let
            returnValue = this.empty;

        for (let i = 0; i < words.length; i++)
        {
            const
                newValue = returnValue + (returnValue.length ? " " : this.empty) + words[i];

            if (newValue.length + elipsis.length > length)
                break;

            returnValue = newValue;
        }

        return returnValue + elipsis;
    }

    // #endregion
}